/**
 * ProviderSelection Component
 *
 * Displays a list of available providers (therapists) for the user to select from.
 * Includes a "No Preference" option for automatic assignment via round-robin.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PublicProvider, PublicProduct } from '../../../../types/product';
import LoadingSpinner from '../../../../components/core/LoadingSpinner';
import UserAvatar from '../../../../components/common/UserAvatar';

interface ProviderSelectionProps {
  /** List of available providers */
  providers: PublicProvider[];
  /** Currently selected provider ID (undefined = not yet selected, null = No Preference, number = specific) */
  selectedProviderId: number | null | undefined;
  /** Callback when a provider is selected */
  onProviderSelect: (providerId: number | null) => void;
  /** The selected service (for displaying price info) */
  selectedService: PublicProduct | null;
  /** Whether providers are loading */
  loading?: boolean;
  /** Error message if providers failed to load */
  error?: string | null;
  /** Callback to go back to service selection */
  onBack?: () => void;
}

/**
 * Format price for display
 */
const formatPrice = (price: number, currency: string): string => {
  if (price === 0) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price);
};

const ProviderSelection: React.FC<ProviderSelectionProps> = ({
  providers,
  selectedProviderId,
  onProviderSelect,
  selectedService,
  loading = false,
  error = null,
  onBack,
}) => {
  const { t } = useTranslation();

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoadingSpinner size="lg" color="primary" className="mb-4" />
        <p className="text-secondary-600">
          {t('booking:serviceBooking.loadingProviders', 'Loading providers...')}
        </p>
      </div>
    );
  }

  // Error state
  /**
   * ⚠️ THE SIGNAL IS RENDERED, NOT THE STRING.
   *
   * `error` carries whatever the fetch failed with — often
   * `error.response.data.message`, written in English for a log, sometimes an
   * axios line like "Request failed with status code 500". Printing it put
   * that in front of a guest on a Japanese booking page. Nothing it can hold
   * is more useful here than "it did not load", which we can at least say in
   * their language.
   */
  if (error) {
    return (
      <div className="alert-danger">
        <p>{t('common:errors.loadingFailed', 'Failed to load data. Please try again.')}</p>
      </div>
    );
  }

  // Empty state (shouldn't happen if service has providers)
  if (!providers || providers.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="h-16 w-16 mx-auto mb-4 text-secondary-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <p className="text-secondary-600">
          {t('booking:serviceBooking.noProvidersAvailable', 'No providers available for this service.')}
        </p>
      </div>
    );
  }

  // Check if "No Preference" is selected (null = No Preference, undefined = not yet selected)
  const isNoPreferenceSelected = selectedProviderId === null;

  return (
    <div className="space-y-4">
      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-secondary-900">
          {t('booking:serviceBooking.selectProvider', 'Select a Provider')}
        </h3>
        {onBack && (
          <button
            onClick={onBack}
            className="inline-flex items-center px-3 py-1 text-sm text-secondary-600 hover:text-secondary-900 transition-colors"
          >
            <svg
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('booking:serviceBooking.changeService', 'Change Service')}
          </button>
        )}
      </div>

      <div className="grid gap-3">
        {/* No Preference Option */}
        <button
          onClick={() => onProviderSelect(null)}
          className={`
            w-full p-4 rounded-lg border-2 text-left transition-all duration-200
            ${isNoPreferenceSelected
              ? 'border-brand bg-brand/5 shadow-sm'
              : 'border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50'
            }
          `}
          style={isNoPreferenceSelected ? { borderColor: 'var(--brand-color, #2E7AA0)' } : undefined}
        >
          <div className="flex items-center gap-4">
            {/* Auto-assign icon (no background) */}
            <div className="w-14 h-14 rounded-full flex-shrink-0 flex items-center justify-center">
              <svg
                className="h-7 w-7"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                style={{ color: 'var(--brand-color, #2E7AA0)' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>

            {/* Details */}
            <div className="flex-1">
              <h4 className="text-base font-medium text-secondary-900">
                {t('booking:serviceBooking.noPreference', 'No Preference')}
              </h4>
              <p className="text-sm text-secondary-600 mt-1">
                {t('booking:serviceBooking.noPreferenceDescription', 'We\'ll assign the best available provider for your time slot.')}
              </p>
            </div>

            {/* Selection indicator - moved outside content div for proper vertical centering */}
            <div
              className={`
                w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center
                ${isNoPreferenceSelected
                  ? 'border-brand bg-brand'
                  : 'border-secondary-300'
                }
              `}
              style={isNoPreferenceSelected ? {
                borderColor: 'var(--brand-color, #2E7AA0)',
                backgroundColor: 'var(--brand-color, #2E7AA0)',
              } : undefined}
            >
              {isNoPreferenceSelected && (
                <svg
                  className="w-3 h-3 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
          </div>
        </button>

        {/* Provider Options */}
        {providers.map((provider) => {
          const isSelected = selectedProviderId === provider.userId;
          const hasAdditionalFee = provider.additionalFee > 0;

          return (
            <button
              key={provider.userId}
              onClick={() => onProviderSelect(provider.userId)}
              className={`
                w-full p-4 rounded-lg border-2 text-left transition-all duration-200
                ${isSelected
                  ? 'border-brand bg-brand/5 shadow-sm'
                  : 'border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50'
                }
              `}
              style={isSelected ? { borderColor: 'var(--brand-color, #2E7AA0)' } : undefined}
            >
              <div className="flex items-center gap-4">
                {/* Provider Avatar */}
                <UserAvatar
                  user={{
                    name: provider.displayName,
                    profilePicture: provider.avatarUrl || undefined,
                  }}
                  size="lg"
                  className="flex-shrink-0"
                />

                {/* Provider Details */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-medium text-secondary-900 truncate">
                    {provider.displayName}
                  </h4>

                  {/* Bio */}
                  {provider.bio && (
                    <p className="text-sm text-secondary-600 mt-1 line-clamp-2">
                      {provider.bio}
                    </p>
                  )}

                  {/* Additional fee info - only show if provider has additional fee */}
                  {hasAdditionalFee && (
                    <div className="flex items-center gap-3 mt-2">
                      <span
                        className="text-sm font-medium"
                        style={{ color: 'var(--brand-color, #2E7AA0)' }}
                      >
                        +{formatPrice(provider.additionalFee, selectedService?.currency || 'USD')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Selection indicator - moved outside content div for proper vertical centering */}
                <div
                  className={`
                    w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center
                    ${isSelected
                      ? 'border-brand bg-brand'
                      : 'border-secondary-300'
                    }
                  `}
                  style={isSelected ? {
                    borderColor: 'var(--brand-color, #2E7AA0)',
                    backgroundColor: 'var(--brand-color, #2E7AA0)',
                  } : undefined}
                >
                  {isSelected && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ProviderSelection;
